import React, { useState, useEffect, useRef, useCallback } from 'react';
import { McToolbar } from "@maersk-global/mds-react-wrapper/components-core/mc-toolbar";
import {
    McDialog,
    McButton,
    McIcon,
    McLoadingIndicator,
    McInput,
    McNotification,
    McTooltip,
    McTabBar,
    McTab,
    McSelect,
    McOption,
    McTextarea,
    McSwitch
} from '@maersk-global/mds-react-wrapper';
import { McSwitchGroup } from '@maersk-global/mds-react-wrapper/components-core/mc-switch-group';
import { NAVAS_N4_DICTIONARY } from '../utils/n4Dictionary';
import { MappingManagerDialog } from './MappingManagerDialog';
import queryService from '../services/queryService';
import agentService from '../services/agentService';
import { TerminalSelect } from './Common/TerminalSelect';
import { DatabaseSelect } from './Common/DatabaseSelect';
import type { SchemaSearchResult, TerminalContextEntry } from '../types';

interface SchemaExplorerProps {
    open: boolean;
    onClose: () => void;
    terminalId: string;
    databaseType?: string;
    /**
     * When provided, jump to this tab whenever it changes (used by toolbar tab shortcuts).
     */
    defaultTab?: 'schema' | 'field-search' | 'field-catalog';
    /**
     * 'modal'    — (default) renders inside an McDialog (existing behavior).
     * 'embedded' — renders inline content suitable for embedding in a panel/drawer.
     *              The `open` and `onClose` props are still respected (parent controls visibility).
     */
    renderMode?: 'modal' | 'embedded';
    /**
     * When provided, table / column / field rows insert their snippet via this callback
     * (typically into a SQL editor) instead of (or in addition to) copying to clipboard.
     */
    onInsertSnippet?: (text: string) => void;
}

interface Column {
    name: string;
    type: string;
    length?: number;
}

interface Table {
    name: string;
    type: 'TABLE' | 'VIEW';
    columns?: Column[];
}

export const SchemaExplorer: React.FC<SchemaExplorerProps> = ({
    open,
    onClose,
    terminalId,
    databaseType = 'ORACLE',
    renderMode = 'modal',
    onInsertSnippet,
    defaultTab,
}) => {
    const latestFetchTables = useRef<() => void>(() => { });
    const latestDatabaseType = useRef(databaseType);

    // Tab state — `defaultTab` drives the active tab when set from outside (toolbar shortcuts)
    const [activeTab, setActiveTab] = useState<'schema' | 'field-search' | 'field-catalog'>(defaultTab ?? 'schema');
    useEffect(() => {
        if (defaultTab) setActiveTab(defaultTab);
    }, [defaultTab]);

    const [selectedTerminal, setSelectedTerminal] = useState(terminalId);
    const [currentDatabaseType, setCurrentDatabaseType] = useState(databaseType);
    const [tables, setTables] = useState<Table[]>([]);
    const [schemaLoaded, setSchemaLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedTable, setExpandedTable] = useState<string | null>(null);
    const [columnsMap, setColumnsMap] = useState<Record<string, Column[]>>({});
    const [loadingColumns, setLoadingColumns] = useState<string | null>(null);
    const [isBusinessView, setIsBusinessView] = useState(false);
    const [userMappings, setUserMappings] = useState<Record<string, any>>({});
    const [showMappingManager, setShowMappingManager] = useState(false);

    // Field search state
    const [fieldSearchTerm, setFieldSearchTerm] = useState('');
    const [fieldSearchDomain, setFieldSearchDomain] = useState('');
    const [fieldSearchResults, setFieldSearchResults] = useState<SchemaSearchResult[]>([]);
    const [fieldSearchLoading, setFieldSearchLoading] = useState(false);
    const [fieldSearchError, setFieldSearchError] = useState<string | null>(null);
    const fieldSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Shared terminal context (loaded when terminal changes)
    const [terminalContextEntries, setTerminalContextEntries] = useState<TerminalContextEntry[]>([]);
    const [terminalContextLoading, setTerminalContextLoading] = useState(false);

    // Field Catalog state
    const [catalogTableSearch, setCatalogTableSearch] = useState('');
    const [catalogExpandedTable, setCatalogExpandedTable] = useState<string | null>(null);
    const [catalogSelectedTable, setCatalogSelectedTable] = useState('');
    const [catalogSelectedColumn, setCatalogSelectedColumn] = useState('');
    const [catalogBusinessName, setCatalogBusinessName] = useState('');
    const [catalogUsageNotes, setCatalogUsageNotes] = useState('');
    const [catalogFkReference, setCatalogFkReference] = useState('');
    const [catalogJoinCondition, setCatalogJoinCondition] = useState('');
    const [catalogDomain, setCatalogDomain] = useState('');
    const [catalogDomainOther, setCatalogDomainOther] = useState('');
    const [catalogFormKey, setCatalogFormKey] = useState(0);
    const [catalogSaveLoading, setCatalogSaveLoading] = useState(false);
    const [catalogSaveSuccess, setCatalogSaveSuccess] = useState(false);
    const [catalogError, setCatalogError] = useState<string | null>(null);
    const catalogFormRefs = {
        bName: useRef<any>(null),
        domainOther: useRef<any>(null),
        fkRef: useRef<any>(null),
        joinRef: useRef<any>(null),
        usageNotes: useRef<any>(null),
        domainSelect: useRef<any>(null)
    };

    const DOMAIN_OPTIONS = ['YARD', 'QUAY', 'CHE', 'GATE', 'RAIL', 'Other'];
    const effectiveDomain = catalogDomain === 'Other' ? catalogDomainOther : catalogDomain;

    const loadTerminalContext = useCallback(async () => {
        if (!selectedTerminal) { setTerminalContextEntries([]); return; }
        setTerminalContextLoading(true);
        try {
            const entries = await agentService.getTerminalFieldContext(selectedTerminal);
            setTerminalContextEntries(entries);
        } catch (e) {
            console.error('Failed to load terminal context', e);
            setTerminalContextEntries([]);
        } finally {
            setTerminalContextLoading(false);
        }
    }, [selectedTerminal]);

    const handleCatalogExpandTable = async (tableName: string) => {
        if (catalogExpandedTable === tableName) { setCatalogExpandedTable(null); return; }
        setCatalogExpandedTable(tableName);
        if (!columnsMap[tableName]) {
            setLoadingColumns(tableName);
            try {
                const columns = await queryService.exploreSchema(selectedTerminal, tableName, latestDatabaseType.current);
                setColumnsMap((prev) => ({ ...prev, [tableName]: columns }));
            } catch (err) {
                console.error(`Failed to fetch columns for ${tableName}`, err);
            } finally {
                setLoadingColumns(null);
            }
        }
    };

    const handleCatalogColumnSelect = (table: string, col: string) => {
        setCatalogSelectedTable(table);
        setCatalogSelectedColumn(col);
        setCatalogFormKey(k => k + 1); // always remount form so web components pick up new values
        const existing = terminalContextEntries.find(e => e.tableName === table && e.columnName === col);
        setCatalogBusinessName(existing?.localAliases?.[0] ?? '');
        setCatalogUsageNotes(existing?.notes ?? '');
        setCatalogFkReference(existing?.fkReference ?? '');
        setCatalogJoinCondition(existing?.joinCondition ?? '');
        const existingDomain = existing?.domain ?? '';
        if (existingDomain && !['YARD', 'QUAY', 'CHE', 'GATE', 'RAIL'].includes(existingDomain)) {
            setCatalogDomain('Other');
            setCatalogDomainOther(existingDomain);
        } else {
            setCatalogDomain(existingDomain);
            setCatalogDomainOther('');
        }
        setCatalogError(null);
    };

    const handleCatalogSave = async () => {
        if (!selectedTerminal || !catalogSelectedTable || !catalogSelectedColumn) return;
        setCatalogSaveLoading(true);
        setCatalogError(null);
        setCatalogSaveSuccess(false);
        try {
            const savedEntry = await agentService.saveTerminalFieldContext(
                selectedTerminal,
                catalogSelectedTable,
                catalogSelectedColumn,
                
                (catalogFormRefs.bName.current?.value ?? catalogBusinessName) ? [catalogFormRefs.bName.current?.value ?? catalogBusinessName] : [],
                (catalogFormRefs.usageNotes.current?.value ?? catalogUsageNotes) || undefined,
                (catalogFormRefs.fkRef.current?.value ?? catalogFkReference) || undefined,
                (catalogFormRefs.joinRef.current?.value ?? catalogJoinCondition) || undefined,
                (catalogFormRefs.domainSelect.current?.value === 'Other' 
                    ? (catalogFormRefs.domainOther.current?.value ?? catalogDomainOther) 
                    : (catalogFormRefs.domainSelect.current?.value ?? effectiveDomain)) || undefined
            );
            // Sync form back from what the DB actually stored
            setCatalogBusinessName(savedEntry.localAliases?.[0] ?? '');
            setCatalogUsageNotes(savedEntry.notes ?? '');
            setCatalogFkReference(savedEntry.fkReference ?? '');
            setCatalogJoinCondition(savedEntry.joinCondition ?? '');
            const savedDom = savedEntry.domain ?? '';
            if (savedDom && !DOMAIN_OPTIONS.slice(0, -1).includes(savedDom)) {
                setCatalogDomain('Other');
                setCatalogDomainOther(savedDom);
            } else {
                setCatalogDomain(savedDom);
                setCatalogDomainOther('');
            }
            await loadTerminalContext();
            setCatalogSaveSuccess(true);
            setTimeout(() => setCatalogSaveSuccess(false), 2500);
        } catch (e: any) {
            setCatalogError(e.response?.data?.message || e.message || 'Save failed');
        } finally {
            setCatalogSaveLoading(false);
        }
    };

    const handleCatalogDelete = async (table: string, col: string) => {
        if (!selectedTerminal) return;
        try {
            await agentService.deleteTerminalFieldContext(selectedTerminal, table, col);
            await loadTerminalContext();
            if (catalogSelectedTable === table && catalogSelectedColumn === col) {
                setCatalogSelectedTable('');
                setCatalogSelectedColumn('');
                setCatalogBusinessName('');
                setCatalogUsageNotes('');
                setCatalogFkReference('');
                setCatalogJoinCondition('');
                setCatalogDomain('');
                setCatalogDomainOther('');
            }
        } catch (e: any) {
            console.error('Failed to delete terminal context entry', e);
        }
    };

    const handleFieldSearch = useCallback(async (term: string, domain: string) => {
        if (!term || term.trim().length < 2) {
            setFieldSearchResults([]);
            return;
        }
        setFieldSearchLoading(true);
        setFieldSearchError(null);
        try {
            const results = await agentService.searchSchemaFields({
                term: term.trim(),
                domain: domain || undefined,
                terminalId: selectedTerminal || undefined,
            });
            setFieldSearchResults(results);
        } catch (err: any) {
            setFieldSearchError(err.response?.data?.message || err.message || 'Search failed');
            setFieldSearchResults([]);
        } finally {
            setFieldSearchLoading(false);
        }
    }, [selectedTerminal]);

    useEffect(() => {
        if (activeTab !== 'field-search') return;
        if (fieldSearchDebounce.current) clearTimeout(fieldSearchDebounce.current);
        fieldSearchDebounce.current = setTimeout(() => {
            handleFieldSearch(fieldSearchTerm, fieldSearchDomain);
        }, 400);
        return () => { if (fieldSearchDebounce.current) clearTimeout(fieldSearchDebounce.current); };
    }, [fieldSearchTerm, fieldSearchDomain, activeTab, handleFieldSearch]);

    useEffect(() => {
        const saved = localStorage.getItem('schema_explorer_mappings');
        if (saved) {
            try {
                setUserMappings(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to parse user mappings", e);
            }
        }
    }, []);

    const getTableDisplay = (tableName: string) => {
        if (!isBusinessView) return { title: tableName, description: null };

        const userMap = userMappings[tableName];
        if (userMap?.businessName) return { title: userMap.businessName, description: NAVAS_N4_DICTIONARY[tableName]?.description };

        const dictEntry = NAVAS_N4_DICTIONARY[tableName];
        if (dictEntry) return { title: dictEntry.title, description: dictEntry.description };

        return { title: tableName, description: null };
    };

    const getColumnDisplay = (tableName: string, colName: string) => {
        if (!isBusinessView) return colName;

        const userMap = userMappings[tableName];
        if (userMap?.columns?.[colName]) return userMap.columns[colName];

        return colName;
    };

    useEffect(() => {
        if (terminalId) {
            setSelectedTerminal(terminalId);
        }
    }, [terminalId]);

    useEffect(() => {
        if (selectedTerminal) loadTerminalContext();
        else setTerminalContextEntries([]);
    }, [selectedTerminal, loadTerminalContext]);

    useEffect(() => {
        if (activeTab === 'field-catalog' && tables.length === 0 && selectedTerminal && !loading) {
            fetchTables();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        latestDatabaseType.current = currentDatabaseType;
    }, [currentDatabaseType]);

    useEffect(() => {
        if (open && selectedTerminal) {
            fetchTables();
        } else if (open && !selectedTerminal) {
            setTables([]);
            // Don't show error immediately, let user select
        }
    }, [open, selectedTerminal, currentDatabaseType]);

    const fetchTables = async () => {
        if (!selectedTerminal) return;

        setSchemaLoaded(false);
        setTables([]); // Clear existing tables before fetching
        setColumnsMap({}); // Clear stale column data when refreshing
        setLoading(true);
        setError(null);
        try {
            const data = await queryService.exploreSchema(selectedTerminal, undefined, latestDatabaseType.current);
            // Ensure unique tables just in case
            const uniqueData = Array.from(new Set(data.map((t: any) => t.name))).map(name => {
                return data.find((t: any) => t.name === name);
            });
            setTables(uniqueData);

            // Pre-populate columnsMap from grouped schema data so that expanding a table
            // never needs a separate API call (which has a bug for Oracle synonyms).
            const preloadedColumns: Record<string, Column[]> = {};
            uniqueData.forEach((table: any) => {
                if (table?.columns && Array.isArray(table.columns) && table.columns.length > 0) {
                    preloadedColumns[table.name] = table.columns;
                }
            });
            if (Object.keys(preloadedColumns).length > 0) {
                setColumnsMap(preloadedColumns);
            }
        } catch (err: any) {
            console.error('Failed to fetch tables', err);
            // Extract the actual error message from the backend
            let msg = 'Failed to load schema.';

            // Try different error response formats
            if (err.response?.data?.message) {
                msg = err.response.data.message;
            } else if (err.response?.data?.error) {
                msg = err.response.data.error;
            } else if (err.response?.data) {
                // Sometimes the error is just a string in data
                msg = typeof err.response.data === 'string' ? err.response.data : msg;
            } else if (err.message) {
                msg = err.message;
            }

            setError(msg);
            setTables([]); // Ensure tables are empty on error
        } finally {
            setLoading(false);
            setSchemaLoaded(true);
        }
    };

    useEffect(() => {
        latestFetchTables.current = fetchTables;
    }, [selectedTerminal, currentDatabaseType]);

    const handleExpandTable = async (tableName: string) => {
        if (expandedTable === tableName) {
            setExpandedTable(null);
            return;
        }

        setExpandedTable(tableName);

        if (!columnsMap[tableName]) {
            setLoadingColumns(tableName);
            try {
                const columns = await queryService.exploreSchema(selectedTerminal, tableName, latestDatabaseType.current);
                setColumnsMap((prev) => ({ ...prev, [tableName]: columns }));
            } catch (err) {
                console.error(`Failed to fetch columns for ${tableName}`, err);
            } finally {
                setLoadingColumns(null);
            }
        }
    };

    const handleCopy = (text: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (onInsertSnippet) {
            onInsertSnippet(text);
        } else {
            navigator.clipboard.writeText(text);
        }
        // Could add toast notification here
    };

    /** Field Intelligence row insert/copy: prefers insertion when caller supplied a handler. */
    const handleFieldInsert = (text: string) => {
        if (onInsertSnippet) {
            onInsertSnippet(text);
        } else {
            navigator.clipboard.writeText(text);
        }
    };

    const isSystemTable = (tableName: string, dbType: string) => {
        const name = tableName.toUpperCase();
        if (dbType === 'ORACLE') {
            return name.startsWith('BIN$') ||
                name.startsWith('AQ$') ||
                name.startsWith('DR$') ||
                name.startsWith('SYS_') ||
                name.startsWith('MDRT_') ||
                name.startsWith('MLOG$_') ||
                name.startsWith('RUPD$_') ||
                name.includes('$'); // Covers most internal/generated tables
        }
        if (dbType === 'POSTGRESQL') {
            return name.startsWith('PG_') || name.startsWith('GP_') || name.startsWith('SQL_');
        }
        return false;
    };

    const filteredTables = tables.filter((table) => {
        // Filter out system tables for both Default and Business views
        if (isSystemTable(table.name, currentDatabaseType)) {
            return false;
        }

        // In Business View, we might want even stricter filtering, but isSystemTable covers the requested "system tables"
        if (isBusinessView) {
            if (table.name.startsWith('ALL_')) { // specific to business view preference
                return false;
            }
        }

        const display = getTableDisplay(table.name);
        const searchLower = searchTerm.toLowerCase();
        const columnNames = [
            ...(columnsMap[table.name] || []),
            ...(table.columns || []),
        ].map((c) => c.name?.toLowerCase?.() || '');
        const fieldMatch = columnNames.some((name) => name.includes(searchLower));

        return table.name.toLowerCase().includes(searchLower) ||
            (display.title && display.title.toLowerCase().includes(searchLower)) ||
            (display.description && display.description.toLowerCase().includes(searchLower)) ||
            fieldMatch;
    });

    // Embedded mode: parent controls visibility via `open`. Hide entirely when closed
    // so the heavy content tree is unmounted (matches modal behavior).
    if (renderMode === 'embedded' && !open) return null;

    const heading = activeTab === 'field-search'
        ? '✦ Field Intelligence'
        : activeTab === 'field-catalog'
            ? '🏷 Field Catalog'
            : (isBusinessView ? 'Data Explorer' : 'Schema Explorer');

    const isEmbedded = renderMode === 'embedded';

    const body = (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: renderMode === 'embedded' ? '0' : '500px' }}>
                {!isEmbedded && (
                    <>
                        {/* Shared terminal selector (above tabs) */}
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', marginBottom: '12px' }}>
                            <div style={{ width: '280px' }}>
                                <TerminalSelect
                                    label="Terminal"
                                    value={selectedTerminal}
                                    placeholder="Select Terminal"
                                    onChange={setSelectedTerminal}
                                />
                            </div>
                            {terminalContextLoading && (
                                <div style={{ paddingBottom: '6px' }}><McLoadingIndicator fit="small" /></div>
                            )}
                        </div>

                        {/* Tab bar */}
                        <div style={{
                            marginBottom: '12px',
                            border: '1px solid var(--mds-color-neutral-20)',
                            borderRadius: '8px',
                            padding: '2px 6px',
                            background: 'var(--mds-color-neutral-05)',
                        }}>
                            <McTabBar
                                currentindex={activeTab === 'schema' ? 0 : activeTab === 'field-search' ? 1 : 2}
                                tabchange={(e: any) => {
                                    const idx = e.detail;
                                    setActiveTab(idx === 0 ? 'schema' : idx === 1 ? 'field-search' : 'field-catalog');
                                }}
                            >
                                <McTab slot="tab" label="Schema Browser" icon="table" />
                                <McTab slot="tab" label="Field Intelligence" icon="magnifying-glass" />
                                <McTab slot="tab" label="Field Catalog" icon="tag" />
                            </McTabBar>
                        </div>
                    </>
                )}

                {/* ─── Field Intelligence Tab ─── */}
                {activeTab === 'field-search' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0', flexGrow: 1, minHeight: 0 }}>

                        {/* Search bar */}
                        <div style={{
                            display: 'flex', gap: '10px', alignItems: 'flex-end',
                            padding: '12px 16px',
                            background: 'var(--mds-color-neutral-05)',
                            borderRadius: '6px',
                            border: '1px solid var(--mds-color-neutral-15)',
                            marginBottom: '10px',
                        }}>
                            <div style={{ flex: 1 }}>
                                <McInput
                                    label="Business Term"
                                    placeholder='e.g. cargo cutoff · vessel ETA · container weight · truck arrival'
                                    value={fieldSearchTerm}
                                    input={(e: any) => setFieldSearchTerm(e.target.value)}
                                >
                                    <McIcon icon="magnifying-glass" slot="prefix" />
                                </McInput>
                            </div>
                            <div style={{ width: '220px', minWidth: '220px', flexShrink: 0 }}>
                                <McSelect
                                                            ref={catalogFormRefs.domainSelect}
                                                            label="Domain"
                                    value={fieldSearchDomain}
                                    optionselected={(e: any) => setFieldSearchDomain(e.detail.value ?? '')}
                                >
                                    <McOption value="">All Domains</McOption>
                                    <McOption value="YARD">Yard</McOption>
                                    <McOption value="QUAY">Quay</McOption>
                                    <McOption value="CHE">CHE</McOption>
                                    <McOption value="GATE">Gate</McOption>
                                </McSelect>
                            </div>
                        </div>

                        {fieldSearchError && (
                            <div style={{ marginBottom: '8px' }}>
                                <McNotification appearance="error" title="Search Error">{fieldSearchError}</McNotification>
                            </div>
                        )}

                        {/* Results panel */}
                        <div style={{
                            flexGrow: 1, overflowY: 'auto', minHeight: 0,
                            border: '1px solid var(--mds-color-neutral-20)',
                            borderRadius: '6px',
                        }}>
                            {fieldSearchLoading && (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px', gap: '12px', color: 'var(--mds-color-neutral-60)' }}>
                                    <McLoadingIndicator fit="medium" />
                                    <span style={{ fontSize: '0.875rem' }}>Searching 8,800+ operational fields…</span>
                                </div>
                            )}
                            {!fieldSearchLoading && fieldSearchTerm.length < 2 && (
                                <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '2rem', marginBottom: '8px', opacity: 0.25 }}>✦</div>
                                    <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--mds-color-neutral-70)', marginBottom: '6px' }}>
                                        Field Intelligence
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--mds-color-neutral-50)', maxWidth: '380px', margin: '0 auto' }}>
                                        Search across 8,800+ Navis N4 schema fields using business language — not raw column names.
                                        Try <em>"cargo cutoff"</em>, <em>"vessel ETA"</em>, or <em>"truck driver"</em>.
                                    </div>
                                </div>
                            )}
                            {!fieldSearchLoading && fieldSearchTerm.length >= 2 && fieldSearchResults.length === 0 && !fieldSearchError && (
                                <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.875rem', color: 'var(--mds-color-neutral-60)', marginBottom: '6px' }}>
                                        No fields matched <strong>"{fieldSearchTerm}"</strong>
                                    </div>
                                    <div style={{ fontSize: '0.775rem', color: 'var(--mds-color-neutral-40)' }}>
                                        Try broader terms or check the domain filter.
                                    </div>
                                </div>
                            )}

                            {!fieldSearchLoading && fieldSearchResults.length > 0 && (() => {
                                // Group results by table for cleaner display
                                const grouped: Record<string, typeof fieldSearchResults> = {};
                                fieldSearchResults.forEach(r => {
                                    if (!grouped[r.tableName]) grouped[r.tableName] = [];
                                    grouped[r.tableName].push(r);
                                });
                                const DOMAIN_STYLE: Record<string, { bg: string; color: string }> = {
                                    QUAY: { bg: '#dff0f8', color: '#0a6e82' },
                                    YARD: { bg: '#e3f4e8', color: '#1e6b30' },
                                    CHE:  { bg: '#fff5e0', color: '#b45309' },
                                    GATE: { bg: '#fce8ec', color: '#9b1d35' },
                                };
                                return Object.entries(grouped).map(([tableName, fields]) => (
                                    <div key={tableName}>
                                        {/* Table header row */}
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                            padding: '7px 16px',
                                            background: 'var(--mds-color-neutral-05)',
                                            borderBottom: '1px solid var(--mds-color-neutral-15)',
                                            borderTop: '1px solid var(--mds-color-neutral-15)',
                                            position: 'sticky', top: 0,
                                        }}>
                                            <McIcon icon="table" size="16" style={{ color: 'var(--mds-color-primary-60)', flexShrink: 0 }} />
                                            <span style={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.04em', color: 'var(--mds-color-neutral-80)', fontFamily: 'monospace' }}>
                                                {tableName}
                                            </span>
                                            {fields[0].tableDescription && (
                                                <span style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-50)', fontStyle: 'italic', marginLeft: '4px' }}>
                                                    — {fields[0].tableDescription}
                                                </span>
                                            )}
                                            <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--mds-color-neutral-40)', background: 'var(--mds-color-neutral-10)', padding: '1px 6px', borderRadius: '10px' }}>
                                                {fields.length} field{fields.length !== 1 ? 's' : ''}
                                            </span>
                                        </div>

                                        {/* Field rows */}
                                        {fields.map((r, i) => (
                                            <div key={i} style={{
                                                display: 'grid',
                                                gridTemplateColumns: '200px 90px 1fr auto',
                                                alignItems: 'start',
                                                gap: '0 12px',
                                                padding: '9px 16px 9px 36px',
                                                borderBottom: '1px solid var(--mds-color-neutral-10)',
                                                transition: 'background 0.1s',
                                            }}
                                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--mds-color-neutral-05)')}
                                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                            >
                                                                {/* Column name + customized overlay */}
                                                <div>
                                                    <span style={{ fontSize: '0.82rem', fontWeight: 600, fontFamily: 'monospace', color: 'var(--mds-color-neutral-85)', wordBreak: 'break-all' }}>
                                                        {r.columnName}
                                                    </span>
                                                    {(() => {
                                                        const entry = terminalContextEntries.find(e => e.tableName === r.tableName && e.columnName === r.columnName);
                                                        if (!entry) return null;
                                                        return (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px', flexWrap: 'wrap' }}>
                                                                <span style={{ fontSize: '0.68rem', fontWeight: 700, background: '#dff0f8', color: '#0a6e82', padding: '1px 7px', borderRadius: '10px' }}>Customized</span>
                                                                {entry.localAliases?.[0] && (
                                                                    <span style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-60)', fontStyle: 'italic' }}>{entry.localAliases[0]}</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>

                                                {/* Type badge */}
                                                <div>
                                                    {r.columnType && (
                                                        <span style={{
                                                            fontSize: '0.68rem', fontFamily: 'monospace',
                                                            padding: '2px 7px', borderRadius: '4px',
                                                            background: 'var(--mds-color-neutral-10)',
                                                            color: 'var(--mds-color-neutral-60)',
                                                            display: 'inline-block', whiteSpace: 'nowrap',
                                                        }}>
                                                            {r.columnType.split('(')[0]}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Business names + join path */}
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                    {r.domains.length > 0 && (
                                                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                            {r.domains.map(d => {
                                                                const ds = DOMAIN_STYLE[d] || { bg: '#eee', color: '#555' };
                                                                return (
                                                                    <span key={d} style={{
                                                                        fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em',
                                                                        padding: '1px 5px', borderRadius: '3px',
                                                                        background: ds.bg, color: ds.color,
                                                                    }}>{d}</span>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    {r.businessNames.length > 0 && (
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-60)', lineHeight: 1.4 }}>
                                                            {r.businessNames.slice(0, 5).map((bn, bi) => {
                                                                const isMatch = bn.toLowerCase().includes(fieldSearchTerm.toLowerCase());
                                                                return (
                                                                    <span key={bi}>
                                                                        {bi > 0 && <span style={{ color: 'var(--mds-color-neutral-30)', margin: '0 3px' }}>·</span>}
                                                                        <span style={isMatch ? { fontWeight: 600, color: 'var(--mds-color-primary-60)' } : {}}>
                                                                            {bn}
                                                                        </span>
                                                                    </span>
                                                                );
                                                            })}
                                                            {r.businessNames.length > 5 && (
                                                                <span style={{ color: 'var(--mds-color-neutral-40)', fontSize: '0.7rem' }}> +{r.businessNames.length - 5}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {r.joinPath && (() => {
                                                        try {
                                                            const jp: Array<{ from_table: string; from_col: string; to_table: string; to_col: string }> = JSON.parse(r.joinPath);
                                                            if (!jp.length) return null;
                                                            return (
                                                                <div style={{ fontSize: '0.7rem', color: 'var(--mds-color-neutral-40)', marginTop: '2px' }}>
                                                                    <McIcon icon="square-arrow-up-right" size="16" style={{ verticalAlign: 'middle', marginRight: '3px' }} />
                                                                    {jp.map((step, si) => (
                                                                        <span key={si}>
                                                                            {si > 0 && <span style={{ margin: '0 3px', opacity: 0.5 }}>→</span>}
                                                                            <span style={{ fontFamily: 'monospace' }}>{step.from_table}.{step.from_col}</span>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            );
                                                        } catch { return null; }
                                                    })()}
                                                </div>

                                                {/* Copy button */}
                                                <div>
                                                    <McTooltip>
                                                        <McButton slot="trigger" variant="plain" fit="small"
                                                            onClick={() => handleFieldInsert(`${r.tableName}.${r.columnName}`)}>
                                                            <McIcon icon={onInsertSnippet ? 'plus-circle' : 'copy'} size="16" />
                                                        </McButton>
                                                        <span>{onInsertSnippet ? 'Insert' : 'Copy'} {r.tableName}.{r.columnName}</span>
                                                    </McTooltip>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ));
                            })()}
                        </div>

                        {/* Footer stats */}
                        {!fieldSearchLoading && fieldSearchResults.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '6px', fontSize: '0.72rem', color: 'var(--mds-color-neutral-50)' }}>
                                <span>
                                    {fieldSearchResults.length} field{fieldSearchResults.length !== 1 ? 's' : ''} across {new Set(fieldSearchResults.map(r => r.tableName)).size} table{new Set(fieldSearchResults.map(r => r.tableName)).size !== 1 ? 's' : ''}
                                </span>
                                {fieldSearchResults.length === 50 && (
                                    <span style={{ color: 'var(--mds-color-feedback-warning-dark)' }}>Limit reached — narrow your search term</span>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Schema Browser Tab */}
                {activeTab === 'schema' && (<>
                <McToolbar style={{ marginBottom: '16px' }}>
                    <div slot="start" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {!isEmbedded && (
                            <div style={{ width: '180px' }}>
                                <DatabaseSelect
                                    label="Database Type"
                                    value={currentDatabaseType as any}
                                    onChange={(val) => setCurrentDatabaseType(val)}
                                    variant="NearRealtime"
                                />
                            </div>
                        )}

                        {selectedTerminal && tables.length > 0 && (
                            <div style={{ width: '300px' }}>
                                <McInput
                                    placeholder={isBusinessView ? "Search data or fields..." : "Search tables or fields..."}
                                    label='Filter'
                                    hiddenlabel
                                    value={searchTerm}
                                    input={(e: any) => setSearchTerm(e.target.value)}
                                    style={{ width: '100%' }}
                                >
                                    <McIcon icon="magnifying-glass" slot="prefix" />
                                </McInput>
                            </div>
                        )}
                    </div>

                    <div slot="end" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <McTooltip>
                            <McButton

                                variant="plain"
                                onClick={() => latestFetchTables.current()}
                                disabled={loading || !selectedTerminal}
                            >
                                <McIcon icon="rotate" />
                            </McButton>
                            <span>Refresh Schema</span>
                        </McTooltip>
                        {isBusinessView && (
                            <McButton variant="plain" onClick={() => setShowMappingManager(true)}>
                                Mappings
                            </McButton>
                        )}


                    </div>
                </McToolbar>

                {error && (
                    <div style={{ padding: '0 0 16px' }}>
                        <McNotification appearance="error" title="Error">
                            {error}
                        </McNotification>
                    </div>
                )}

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                        <McLoadingIndicator fit="medium" />
                    </div>
                ) : (
                    <div style={{ flexGrow: 1, overflowY: 'auto', border: '1px solid var(--mds-color-neutral-20)', borderRadius: '4px' }}>
                        {!selectedTerminal && (
                            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--mds-color-neutral-60)' }}>
                                {isEmbedded
                                    ? "Select a terminal from the top toolbar to view tables."
                                    : (isBusinessView ? "Select a data source to view data." : "Select a terminal to view tables.")}
                            </div>
                        )}
                        {selectedTerminal && filteredTables.length === 0 && !error && (
                            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--mds-color-neutral-60)' }}>
                                {schemaLoaded ? 'No tables found.' : 'Loading schema...'}
                            </div>
                        )}
                        {filteredTables.map((table) => {
                            const display = getTableDisplay(table.name);
                            return (
                                <div key={table.name} style={{ borderBottom: '1px solid var(--mds-color-neutral-20)' }}>
                                    <div
                                        style={{
                                            padding: '12px 16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            cursor: 'pointer',
                                            backgroundColor: expandedTable === table.name ? 'var(--mds-color-neutral-10)' : 'transparent'
                                        }}
                                        onClick={() => handleExpandTable(table.name)}
                                    >
                                        <McIcon
                                            icon={table.type === 'VIEW' ? 'eye' : 'table'}
                                            style={{ color: table.type === 'VIEW' ? 'var(--mds-color-neutral-60)' : 'var(--mds-color-primary-60)' }}
                                        />
                                        <div style={{ flexGrow: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 500, fontSize: '0.9rem', wordBreak: 'break-all' }}>
                                                {display.title}
                                            </div>
                                            {isBusinessView && display.description && (
                                                <div style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-60)', marginTop: '2px' }}>
                                                    {display.description}
                                                </div>
                                            )}
                                            {!isBusinessView && (
                                                <div style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-60)' }}>
                                                    {table.type}
                                                </div>
                                            )}
                                            {isBusinessView && (
                                                <div style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-60)' }}>
                                                    {table.type === 'TABLE' ? 'Data Entity' : 'Data View'}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <McTooltip>
                                                <McButton
                                                    slot="trigger"
                                                    variant="plain"
                                                    fit="small"
                                                    onClick={(e: any) => handleCopy(table.name, e)}
                                                >
                                                    <McIcon icon={onInsertSnippet ? 'plus-circle' : 'copy'} size="20" />
                                                </McButton>
                                                <span>{onInsertSnippet ? 'Insert Table Name' : 'Copy Table Name'}</span>
                                            </McTooltip>
                                            <McIcon icon={expandedTable === table.name ? 'chevron-down' : 'chevron-right'} />
                                        </div>
                                    </div>

                                    {expandedTable === table.name && (
                                        <div style={{ backgroundColor: 'var(--mds-color-neutral-05)', paddingLeft: '40px' }}>
                                            {loadingColumns === table.name ? (
                                                <div style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
                                                    <McLoadingIndicator fit="small" />
                                                </div>
                                            ) : (
                                                <div>
                                                    {columnsMap[table.name]?.map((col) => (
                                                        <div key={col.name} style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            padding: '8px 16px 8px 0',
                                                            borderBottom: '1px solid var(--mds-color-neutral-10)'
                                                        }}>
                                                            <McIcon icon="stack" size="16" style={{ color: 'var(--mds-color-neutral-40)' }} />
                                                            <div style={{ flexGrow: 1, minWidth: 0 }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                                                    <span style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                                                                        {getColumnDisplay(table.name, col.name)}
                                                                    </span>
                                                                    {!isBusinessView && (
                                                                        <span style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-60)', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                                                                            {col.type}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <McTooltip>
                                                                <McButton
                                                                    slot="trigger"
                                                                    variant="plain"
                                                                    fit="small"
                                                                    onClick={(e: any) => handleCopy(`${table.name}.${col.name}`, e)}
                                                                    style={{ height: '24px', width: '24px' } as React.CSSProperties}
                                                                >
                                                                    <McIcon icon={onInsertSnippet ? 'plus-circle' : 'copy'} size="16" />
                                                                </McButton>
                                                                <span>{onInsertSnippet ? `Insert ${table.name}.${col.name}` : 'Copy Column'}</span>
                                                            </McTooltip>
                                                        </div>
                                                    ))}
                                                    {(!columnsMap[table.name] || columnsMap[table.name].length === 0) && (
                                                        <div style={{ padding: '12px', fontSize: '0.85rem', color: 'var(--mds-color-neutral-60)' }}>
                                                            No columns found
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                </>)}

                {/* ─── Field Catalog Tab ─── */}
                {activeTab === 'field-catalog' && (
                    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, gap: '8px' }}>
                        {!selectedTerminal ? (
                            <div style={{ padding: '60px', textAlign: 'center', color: 'var(--mds-color-neutral-50)' }}>
                                Select a terminal above to manage field tags.
                            </div>
                        ) : (
                            <>
                                {/* ── SECTION 1: Browse & Tag (fixed height) ── */}
                                <div style={{
                                    display: 'flex', flexShrink: 0, height: '320px',
                                    border: '1px solid var(--mds-color-neutral-20)',
                                    borderRadius: '6px', overflow: 'hidden',
                                }}>
                                    {/* Left: table/column browser */}
                                    <div style={{
                                        width: '260px', flexShrink: 0,
                                        borderRight: '1px solid var(--mds-color-neutral-20)',
                                        display: 'flex', flexDirection: 'column', overflow: 'hidden',
                                        background: 'var(--mds-color-neutral-02, #fafafa)',
                                    }}>
                                        {/* Browser header */}
                                        <div style={{
                                            padding: '8px', borderBottom: '1px solid var(--mds-color-neutral-10)',
                                            background: 'var(--mds-color-neutral-05)',
                                            fontSize: '0.7rem', fontWeight: 700,
                                            color: 'var(--mds-color-neutral-60)',
                                            textTransform: 'uppercase', letterSpacing: '0.05em',
                                            display: 'flex', alignItems: 'center', gap: '6px',
                                        }}>
                                            <McIcon icon="table" size="16" />
                                            Browse Tables
                                        </div>
                                        <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--mds-color-neutral-10)' }}>
                                            <McInput
                                                label="Filter tables"
                                                hiddenlabel
                                                placeholder="Filter tables…"
                                                value={catalogTableSearch}
                                                input={(e: any) => setCatalogTableSearch(e.target.value)}
                                            >
                                                <McIcon icon="magnifying-glass" slot="prefix" />
                                            </McInput>
                                        </div>
                                        <div style={{ flex: 1, overflowY: 'auto' }}>
                                            {loading && (
                                                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}><McLoadingIndicator fit="small" /></div>
                                            )}
                                            {tables
                                                .filter(t => !isSystemTable(t.name, currentDatabaseType))
                                                .filter(t => t.name.toLowerCase().includes(catalogTableSearch.toLowerCase()))
                                                .map(table => {
                                                    const isTaggedTable = terminalContextEntries.some(e => e.tableName === table.name);
                                                    const isExpanded = catalogExpandedTable === table.name;
                                                    const cols = columnsMap[table.name] || [];
                                                    return (
                                                        <div key={table.name}>
                                                            <div
                                                                onClick={() => handleCatalogExpandTable(table.name)}
                                                                style={{
                                                                    display: 'flex', alignItems: 'center', gap: '6px',
                                                                    padding: '6px 10px', cursor: 'pointer',
                                                                    background: isExpanded ? 'var(--mds-color-neutral-10)' : 'transparent',
                                                                    borderBottom: '1px solid var(--mds-color-neutral-10)',
                                                                }}
                                                                onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.background = 'var(--mds-color-neutral-05)'; }}
                                                                onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                                            >
                                                                <McIcon icon="table" size="16" style={{ color: 'var(--mds-color-primary-60)', flexShrink: 0 }} />
                                                                <span style={{ fontSize: '0.78rem', fontFamily: 'monospace', fontWeight: 500, flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{table.name}</span>
                                                                {isTaggedTable && <McIcon icon="tag" size="16" style={{ color: '#0a6e82', flexShrink: 0 }} />}
                                                                <McIcon icon={isExpanded ? 'chevron-down' : 'chevron-right'} size="16" style={{ flexShrink: 0 }} />
                                                            </div>
                                                            {isExpanded && (
                                                                <div>
                                                                    {loadingColumns === table.name && (
                                                                        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px' }}><McLoadingIndicator fit="small" /></div>
                                                                    )}
                                                                    {cols.map(col => {
                                                                        const isTagged = terminalContextEntries.some(e => e.tableName === table.name && e.columnName === col.name);
                                                                        const isSelected = catalogSelectedTable === table.name && catalogSelectedColumn === col.name;
                                                                        return (
                                                                            <div
                                                                                key={col.name}
                                                                                onClick={() => handleCatalogColumnSelect(table.name, col.name)}
                                                                                style={{
                                                                                    display: 'flex', alignItems: 'center', gap: '6px',
                                                                                    padding: '5px 10px 5px 22px',
                                                                                    cursor: 'pointer',
                                                                                    borderBottom: '1px solid var(--mds-color-neutral-05)',
                                                                                    background: isSelected ? '#e8f4f8' : 'transparent',
                                                                                    borderLeft: isSelected ? '3px solid #0a6e82' : '3px solid transparent',
                                                                                }}
                                                                                onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'var(--mds-color-neutral-05)'; }}
                                                                                onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                                                            >
                                                                                <McIcon icon="stack" size="16" style={{ color: 'var(--mds-color-neutral-40)', flexShrink: 0 }} />
                                                                                <span style={{ fontSize: '0.76rem', fontFamily: 'monospace', flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.name}</span>
                                                                                {isTagged && <McIcon icon="tag" size="16" style={{ color: '#0a6e82', flexShrink: 0 }} />}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    {cols.length === 0 && loadingColumns !== table.name && (
                                                                        <div style={{ padding: '6px 10px 6px 22px', fontSize: '0.72rem', color: 'var(--mds-color-neutral-40)' }}>No columns</div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>

                                    {/* Right: edit form */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                        {!catalogSelectedColumn ? (
                                            /* Empty state */
                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--mds-color-neutral-40)', gap: '8px' }}>
                                                <div style={{ fontSize: '2rem', opacity: 0.25 }}>🏷</div>
                                                <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--mds-color-neutral-60)' }}>Select a field to tag</div>
                                                <div style={{ fontSize: '0.78rem', color: 'var(--mds-color-neutral-40)' }}>Expand a table on the left and click a column.</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-40)', marginTop: '4px' }}>Or click any row in the table below to edit it.</div>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Form header: field identity + status + close button */}
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                    padding: '10px 16px',
                                                    background: 'var(--mds-color-neutral-05)',
                                                    borderBottom: '1px solid var(--mds-color-neutral-15)',
                                                    flexShrink: 0,
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                                        <div>
                                                            <div style={{ fontSize: '0.65rem', color: 'var(--mds-color-neutral-50)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Table</div>
                                                            <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', color: 'var(--mds-color-neutral-90)' }}>{catalogSelectedTable}</div>
                                                        </div>
                                                        <div style={{ color: 'var(--mds-color-neutral-30)', fontSize: '1rem' }}>›</div>
                                                        <div>
                                                            <div style={{ fontSize: '0.65rem', color: 'var(--mds-color-neutral-50)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Column</div>
                                                            <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', color: '#0a6e82' }}>{catalogSelectedColumn}</div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            {(() => {
                                                                const existing = terminalContextEntries.find(e => e.tableName === catalogSelectedTable && e.columnName === catalogSelectedColumn);
                                                                if (!existing) return <span style={{ fontSize: '0.7rem', color: 'var(--mds-color-neutral-40)', fontStyle: 'italic' }}>Not tagged yet</span>;
                                                                return existing.isStandardField
                                                                    ? <span style={{ fontSize: '0.68rem', fontWeight: 700, background: '#dff0f8', color: '#0a6e82', padding: '2px 8px', borderRadius: '10px' }}>Customized</span>
                                                                    : <span style={{ fontSize: '0.68rem', fontWeight: 700, background: '#fff5e0', color: '#b45309', padding: '2px 8px', borderRadius: '10px' }}>Unique ⚡</span>;
                                                            })()}
                                                            {catalogSaveSuccess && (
                                                                <span style={{ fontSize: '0.68rem', fontWeight: 700, background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: '10px', transition: 'opacity 0.3s' }}>
                                                                    ✓ Saved
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {/* Close / clear button */}
                                                    <McButton
                                                        variant="plain"
                                                        fit="small"
                                                        title="Close editor"
                                                        onClick={() => {
                                                            setCatalogSelectedTable('');
                                                            setCatalogSelectedColumn('');
                                                            setCatalogBusinessName('');
                                                            setCatalogUsageNotes('');
                                                            setCatalogFkReference('');
                                                            setCatalogJoinCondition('');
                                                            setCatalogDomain('');
                                                            setCatalogDomainOther('');
                                                            setCatalogError('');
                                                        }}
                                                    >
                                                        <McIcon icon="times" size="20" />
                                                    </McButton>
                                                </div>

                                                {/* Form body: key counter forces full remount on every row click so MDS web components pick up new values */}
                                                <div key={catalogFormKey} style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                                    {/* Row 1: Business Name | Domain */}
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '12px', alignItems: 'end' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                            <McInput
                                                            label='Business Name'
                                                                ref={catalogFormRefs.bName}
                                                                placeholder="e.g. Cargo Cutoff Date"
                                                                value={catalogBusinessName}
                                                                input={(e: any) => setCatalogBusinessName(e.target.value)}
                                                            />
                                                        </div>
                                                        <McSelect
                                                            ref={catalogFormRefs.domainSelect}
                                                            label="Domain"
                                                            value={catalogDomain}
                                                            placeholder="Select…"
                                                            optionselected={(e: any) => {
                                                                setCatalogDomain(e.detail.value ?? '');
                                                                if (e.detail.value !== 'Other') setCatalogDomainOther('');
                                                            }}
                                                        >
                                                            {DOMAIN_OPTIONS.map(d => (
                                                                <McOption key={d} value={d}>{d}</McOption>
                                                            ))}
                                                        </McSelect>
                                                    </div>
                                                    {/* Custom domain — only when Other is selected */}
                                                    {catalogDomain === 'Other' && (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                            <McInput
                                                            label='Custom Domain'
                                                                ref={catalogFormRefs.domainOther}
                                                                placeholder="e.g. CUSTOMS"
                                                                value={catalogDomainOther}
                                                                input={(e: any) => setCatalogDomainOther(e.target.value)}
                                                            />
                                                        </div>
                                                    )}
                                                    {/* Row 2: FK Reference | Join Condition */}
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                            <McInput
                                                            label='FK Reference'
                                                                ref={catalogFormRefs.fkRef}
                                                                placeholder="e.g. vessel_visit.gkey"
                                                                value={catalogFkReference}
                                                                input={(e: any) => setCatalogFkReference(e.target.value)}
                                                            />
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                            <McInput
                                                            label='Join Condition'
                                                                ref={catalogFormRefs.joinRef}
                                                                placeholder="e.g. a.gkey = b.vessel_gkey"
                                                                value={catalogJoinCondition}
                                                                input={(e: any) => setCatalogJoinCondition(e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                    {/* Row 3: Usage / Notes */}
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                        <McTextarea
                                                        label='Usage / Notes'
                                                            ref={catalogFormRefs.usageNotes}
                                                            placeholder="What is this field used for at this terminal?"
                                                            value={catalogUsageNotes}
                                                            input={(e: any) => setCatalogUsageNotes(e.target.value)}
                                                        />
                                                    </div>
                                                    {catalogError && (
                                                        <McNotification appearance="error" title="Error">{catalogError}</McNotification>
                                                    )}
                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                        <McButton
                                                            appearance="primary"
                                                            fit="small"
                                                            onClick={handleCatalogSave}
                                                            disabled={catalogSaveLoading || (!catalogBusinessName.trim() && !catalogUsageNotes.trim())}
                                                        >
                                                            {catalogSaveLoading ? <McLoadingIndicator fit="small" /> : 'Save Tag'}
                                                        </McButton>
                                                        {terminalContextEntries.some(e => e.tableName === catalogSelectedTable && e.columnName === catalogSelectedColumn) && (
                                                            <McButton
                                                                appearance="error"
                                                                variant="outlined"
                                                                fit="small"
                                                                onClick={() => handleCatalogDelete(catalogSelectedTable, catalogSelectedColumn)}
                                                            >
                                                                Remove Tag
                                                            </McButton>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* ── SECTION 2: Tagged Fields (fills remaining space, always visible) ── */}
                                <div style={{
                                    flex: 1, minHeight: 0,
                                    display: 'flex', flexDirection: 'column',
                                    border: '1px solid var(--mds-color-neutral-20)',
                                    borderRadius: '6px', overflow: 'hidden',
                                }}>
                                    {/* Table header bar */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '8px 12px',
                                        background: 'var(--mds-color-neutral-05)',
                                        borderBottom: '1px solid var(--mds-color-neutral-15)',
                                        flexShrink: 0,
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <McIcon icon="tag" size="16" style={{ color: '#0a6e82' }} />
                                            <span style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--mds-color-neutral-80)' }}>
                                                Tagged Fields — {selectedTerminal}
                                            </span>
                                            <span style={{
                                                fontSize: '0.72rem', fontWeight: 700,
                                                background: terminalContextEntries.length ? '#0a6e82' : 'var(--mds-color-neutral-20)',
                                                color: terminalContextEntries.length ? '#fff' : 'var(--mds-color-neutral-60)',
                                                padding: '1px 7px', borderRadius: '10px',
                                            }}>
                                                {terminalContextEntries.length}
                                            </span>
                                        </div>
                                        {terminalContextEntries.length > 0 && (
                                            <span style={{ fontSize: '0.72rem', color: 'var(--mds-color-neutral-50)', fontStyle: 'italic' }}>
                                                Click a row to edit
                                            </span>
                                        )}
                                    </div>

                                    {terminalContextEntries.length === 0 ? (
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '6px', color: 'var(--mds-color-neutral-40)' }}>
                                            <div style={{ fontSize: '1.5rem', opacity: 0.2 }}>🏷</div>
                                            <div style={{ fontSize: '0.82rem' }}>No fields tagged yet for this terminal.</div>
                                            <div style={{ fontSize: '0.75rem' }}>Use the browser above to select a column and save a tag.</div>
                                        </div>
                                    ) : (
                                        <div style={{ flex: 1, overflowY: 'auto' }}>
                                            {/* Column headers */}
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: '160px 150px 1fr 100px 160px 110px 36px',
                                                gap: '0 8px',
                                                padding: '5px 12px',
                                                position: 'sticky', top: 0, zIndex: 1,
                                                background: 'var(--mds-color-neutral-10)',
                                                borderBottom: '1px solid var(--mds-color-neutral-20)',
                                                fontSize: '0.68rem',
                                                fontWeight: 700,
                                                color: 'var(--mds-color-neutral-60)',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.04em',
                                            }}>
                                                <span>Table</span>
                                                <span>Column</span>
                                                <span>Business Name</span>
                                                <span>Domain</span>
                                                <span>FK Reference</span>
                                                <span>Status</span>
                                                <span />
                                            </div>
                                            {/* Rows */}
                                            {terminalContextEntries.map((entry, i) => {
                                                const isActive = catalogSelectedTable === entry.tableName && catalogSelectedColumn === entry.columnName;
                                                return (
                                                    <div
                                                        key={i}
                                                        onClick={() => handleCatalogColumnSelect(entry.tableName, entry.columnName)}
                                                        style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: '160px 150px 1fr 100px 160px 110px 36px',
                                                            gap: '0 8px',
                                                            padding: '7px 12px',
                                                            alignItems: 'center',
                                                            cursor: 'pointer',
                                                            borderBottom: '1px solid var(--mds-color-neutral-10)',
                                                            background: isActive ? '#e8f4f8' : 'transparent',
                                                            borderLeft: isActive ? '3px solid #0a6e82' : '3px solid transparent',
                                                            transition: 'background 0.1s',
                                                        }}
                                                        onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'var(--mds-color-neutral-05)'; }}
                                                        onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                                    >
                                                        <span style={{ fontSize: '0.74rem', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--mds-color-neutral-70)' }}>{entry.tableName}</span>
                                                        <span style={{ fontSize: '0.74rem', fontFamily: 'monospace', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#0a6e82' }}>{entry.columnName}</span>
                                                        <span style={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.localAliases?.[0] || <span style={{ color: 'var(--mds-color-neutral-30)' }}>—</span>}</span>
                                                        <span style={{ fontSize: '0.74rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--mds-color-neutral-60)' }}>{entry.domain || <span style={{ color: 'var(--mds-color-neutral-30)' }}>—</span>}</span>
                                                        <span style={{ fontSize: '0.72rem', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--mds-color-neutral-50)' }}>{entry.fkReference || <span style={{ color: 'var(--mds-color-neutral-30)' }}>—</span>}</span>
                                                        <span>
                                                            {entry.isStandardField
                                                                ? <span style={{ fontSize: '0.67rem', fontWeight: 700, background: '#dff0f8', color: '#0a6e82', padding: '2px 7px', borderRadius: '10px' }}>Customized</span>
                                                                : <span style={{ fontSize: '0.67rem', fontWeight: 700, background: '#fff5e0', color: '#b45309', padding: '2px 7px', borderRadius: '10px' }}>Unique ⚡</span>
                                                            }
                                                        </span>
                                                        <McButton
                                                            variant="plain"
                                                            fit="small"
                                                            title="Remove tag"
                                                            onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); handleCatalogDelete(entry.tableName, entry.columnName); }}
                                                        >
                                                            <McIcon icon="trash" size="16" />
                                                        </McButton>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}

            </div>

            <MappingManagerDialog
                open={showMappingManager}
                onClose={() => setShowMappingManager(false)}
                currentMappings={userMappings}
                onSave={(newMappings) => {
                    setUserMappings(newMappings);
                    try {
                        localStorage.setItem('schema_explorer_mappings', JSON.stringify(newMappings));
                    } catch (e) {
                        console.error("Failed to save mappings", e);
                    }
                }}
                availableTables={tables.map(t => t.name)}
                terminalId={selectedTerminal}
            />
        </>
    );

    if (renderMode === 'embedded') {
        return body;
    }

    return (
        <McDialog
            open={open}
            closed={onClose}
            heading={heading}
            width="100vw"
            height="100vh"
        >
            {body}
        </McDialog>
    );
};
